import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppShell } from '@/components/AppShell';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { TRACKS, TRACK_LABELS } from '@/lib/tracks';
import { api } from '@/lib/api';
import { ArrowRight, BookOpen, Atom, FlaskConical, Flame, Sparkles, History as HistoryIcon, TrendingUp, Gift } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

const TRACK_ICONS = {
  BOARDS: BookOpen,
  JEE: Atom,
  NEET: FlaskConical,
};

export default function DashboardPage() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [recent, setRecent] = useState([]);
  const [entitlements, setEntitlements] = useState(null);

  useEffect(() => {
    api
      .get('/test/history/list')
      .then(({ data }) => setRecent((data.sessions || []).slice(0, 3)))
      .catch(() => {});
    api.get('/entitlements').then(({ data }) => setEntitlements(data)).catch(() => {});
  }, []);

  const stats = React.useMemo(() => {
    const submitted = recent.filter((s) => s.status === 'submitted');
    const attempts = submitted.length;
    const avg = attempts
      ? Math.round(submitted.reduce((a, s) => a + (s.percentage || 0), 0) / attempts)
      : 0;
    const best = attempts ? Math.max(...submitted.map((s) => s.percentage || 0)) : 0;
    return { attempts, avg, best };
  }, [recent]);

  return (
    <AppShell>
      <div data-testid="dashboard-page" className="space-y-8">
        {/* Hero */}
        <section className="relative overflow-hidden rounded-2xl border border-white/10 bg-black text-white">
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0"
            style={{
              backgroundImage:
                'radial-gradient(600px circle at 10% 0%, rgba(220,38,38,0.32), transparent 55%), radial-gradient(500px circle at 100% 100%, rgba(239,68,68,0.14), transparent 60%)',
            }}
          />
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 opacity-[0.15]"
            style={{
              backgroundImage:
                'linear-gradient(rgba(255,255,255,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.06) 1px, transparent 1px)',
              backgroundSize: '48px 48px',
              maskImage: 'radial-gradient(ellipse at center, black 30%, transparent 75%)',
            }}
          />
          <div className="relative px-5 py-8 sm:px-10 sm:py-12">
            <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-red-500/40 bg-red-500/10 px-3 py-1 text-xs font-medium text-red-300">
              <Flame className="h-3.5 w-3.5" />
              Hello{user?.name ? `, ${user.name.split(' ')[0]}` : ''} — ready to grind?
            </div>
            <h1 className="max-w-3xl font-display text-3xl font-bold leading-tight sm:text-5xl">
              Pick a chapter.{' '}
              <span className="bg-gradient-to-b from-red-300 to-red-600 bg-clip-text text-transparent">
                Break your last score.
              </span>
            </h1>
            <p className="mt-3 max-w-2xl text-sm text-white/70 sm:text-base">
              35 fresh AI-crafted questions per set · Set 3 chapterwise PDF for JEE / NEET · Live timer · AI report card with weak-topic map.
            </p>

            <div className="mt-6 grid max-w-2xl grid-cols-3 gap-3">
              <MiniStat kpi={stats.attempts} label="Recent attempts" />
              <MiniStat kpi={stats.attempts ? `${stats.avg}%` : '—'} label="Recent average" />
              <MiniStat kpi={stats.attempts ? `${Math.round(stats.best)}%` : '—'} label="Personal best" />
            </div>
          </div>
        </section>

        {entitlements?.bonus_credits > 0 && (
          <section>
            <div className="flex flex-col items-start justify-between gap-3 rounded-2xl border border-emerald-500/40 bg-emerald-500/5 p-4 sm:flex-row sm:items-center sm:p-5">
              <div className="flex items-center gap-3">
                <div className="grid h-11 w-11 place-items-center rounded-lg bg-emerald-600 text-white shadow-[0_0_24px_rgba(16,185,129,0.35)]">
                  <Gift className="h-5 w-5" />
                </div>
                <div>
                  <div className="font-display text-lg font-semibold">
                    You have {entitlements.bonus_credits} referral credit{entitlements.bonus_credits > 1 ? 's' : ''} to spend
                  </div>
                  <div className="text-sm text-muted-foreground">
                    Open any chapter and hit “Redeem referral credit”. Or refer more friends to stack them.
                  </div>
                </div>
              </div>
              <Button
                onClick={() => nav('/referral')}
                className="gap-1.5"
                data-testid="dashboard-open-referral"
              >
                <Gift className="h-4 w-4" />
                Refer more friends
              </Button>
            </div>
          </section>
        )}

        {/* Tracks */}
        <section>
          <div className="mb-4 flex items-end justify-between">
            <div>
              <h2 className="font-display text-xl font-semibold">Select your track</h2>
              <p className="text-sm text-muted-foreground">Same NCERT syllabus. Different exam DNA.</p>
            </div>
            <Sparkles className="hidden h-5 w-5 text-red-500 sm:block" />
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {TRACKS.map((t, i) => {
              const Icon = TRACK_ICONS[t.key] || BookOpen;
              return (
                <Card
                  key={t.key}
                  className="group relative overflow-hidden border-2 border-transparent transition-all hover:border-red-500/40 hover:shadow-[0_20px_50px_-20px_rgba(239,68,68,0.35)] hover:-translate-y-1"
                  data-testid={`track-card-${t.key.toLowerCase()}`}
                >
                  <div
                    className="absolute left-0 top-0 h-full w-1.5"
                    style={{ backgroundColor: t.color }}
                    aria-hidden
                  />
                  <CardContent className="space-y-4 p-5 sm:p-6">
                    <div className="flex items-center gap-3">
                      <div
                        className="grid h-12 w-12 place-items-center rounded-lg text-white shadow-md"
                        style={{ backgroundColor: t.color }}
                      >
                        <Icon className="h-6 w-6" />
                      </div>
                      <div>
                        <div className="font-display text-lg font-semibold">{t.title}</div>
                        <div className="text-xs text-muted-foreground">{t.subtitle}</div>
                      </div>
                      {i === 1 && (
                        <Badge className="ml-auto bg-red-600 text-white hover:bg-red-500">
                          Popular
                        </Badge>
                      )}
                    </div>
                    <p className="min-h-[3rem] text-sm text-muted-foreground">{t.desc}</p>
                    <Button
                      onClick={() => {
                        if (t.key === 'BOARDS') nav('/boards');
                        else nav(`/pick?track=${t.key}`);
                      }}
                      className="w-full gap-1.5 bg-black text-white hover:bg-red-600"
                      data-testid={`track-cta-${t.key.toLowerCase()}`}
                    >
                      Choose chapter <ArrowRight className="h-4 w-4" />
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </section>

        {/* Q-Bank — Highest Seller */}
        <section>
          <div className="mb-4 flex items-end justify-between">
            <div>
              <h2 className="font-display text-xl font-semibold">Class 12 Q-Bank</h2>
              <p className="text-sm text-muted-foreground">Curated PDFs across all 5 board subjects.</p>
            </div>
            <Flame className="hidden h-5 w-5 text-red-500 sm:block" />
          </div>
          <Card
            className="group relative cursor-pointer overflow-hidden border-2 border-red-500/25 bg-gradient-to-br from-red-500/12 via-red-500/5 to-transparent transition-all hover:-translate-y-0.5 hover:border-red-500/50 hover:shadow-[0_20px_50px_-20px_rgba(239,68,68,0.5)]"
            onClick={() => nav('/qbank')}
            data-testid="qbank-home-card"
          >
            <div
              aria-hidden
              className="pointer-events-none absolute -right-24 -top-16 h-64 w-64 rounded-full bg-red-500/20 blur-[90px]"
            />
            <CardContent className="relative flex flex-wrap items-center gap-6 p-5 sm:p-6">
              <div className="grid h-14 w-14 place-items-center rounded-xl bg-red-600 text-white shadow-[0_0_25px_rgba(239,68,68,0.55)]">
                <BookOpen className="h-7 w-7" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="mb-1 flex flex-wrap items-center gap-2">
                  <div className="font-display text-lg font-semibold sm:text-xl">Boards Q-Bank · Class 12</div>
                  <Badge className="gap-1 bg-red-600 text-white shadow-[0_0_14px_rgba(239,68,68,0.5)] hover:bg-red-500">
                    <Flame className="h-3 w-3" />
                    Highest Seller
                  </Badge>
                </div>
                <div className="text-sm text-muted-foreground">
                  Physics · Chemistry · Maths · Biology · English — one Drive link per subject with a free sample.
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="hidden text-right sm:block">
                  <div className="text-[11px] uppercase tracking-wider text-muted-foreground">One-time</div>
                  <div className="font-display text-2xl font-bold">₹999</div>
                </div>
                <Button
                  className="gap-1.5 bg-red-600 hover:bg-red-500"
                  onClick={(e) => {
                    e.stopPropagation();
                    nav('/qbank');
                  }}
                  data-testid="qbank-home-cta"
                >
                  Explore Q-Bank <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Recent activity */}
        <section>
          <div className="mb-4 flex items-end justify-between">
            <div>
              <h2 className="font-display text-xl font-semibold">Recent attempts</h2>
              <p className="text-sm text-muted-foreground">Continue where you left off.</p>
            </div>
            <Button variant="ghost" size="sm" onClick={() => nav('/history')} className="gap-1.5">
              <HistoryIcon className="h-4 w-4" />
              All history
            </Button>
          </div>
          {recent.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="flex flex-col items-center gap-3 p-8 text-center">
                <div className="grid h-12 w-12 place-items-center rounded-full bg-red-100 text-red-600">
                  <TrendingUp className="h-6 w-6" />
                </div>
                <div className="font-display text-lg font-semibold">No attempts yet</div>
                <div className="text-sm text-muted-foreground">Start with your favourite track above.</div>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-3 md:grid-cols-3">
              {recent.map((r) => (
                <Card
                  key={r.id}
                  className="group cursor-pointer transition-shadow hover:shadow-md"
                  onClick={() =>
                    nav(r.status === 'submitted' ? `/report/${r.id}` : `/test/${r.id}`)
                  }
                >
                  <CardContent className="space-y-2 p-4">
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary" className="text-xs">
                        {TRACK_LABELS[r.track] || r.track}
                      </Badge>
                      <Badge variant="outline" className="text-xs">Set {r.set_number}</Badge>
                    </div>
                    <div className="line-clamp-2 font-medium">{r.chapter}</div>
                    <div className="text-xs text-muted-foreground">
                      Class {r.klass} · {r.subject}
                    </div>
                    <div className="pt-1">
                      {r.status === 'submitted' ? (
                        <div className="flex items-baseline gap-2">
                          <span className="font-mono-ui text-2xl font-bold text-red-600">
                            {Math.round(r.percentage || 0)}%
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {r.correct}/{r.total}
                          </span>
                        </div>
                      ) : (
                        <Badge variant="outline" className="text-xs">In progress</Badge>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </section>
      </div>
    </AppShell>
  );
}

function MiniStat({ kpi, label }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.04] p-3 backdrop-blur">
      <div className="font-display text-xl font-bold sm:text-2xl">
        <span className="bg-gradient-to-b from-red-300 to-red-500 bg-clip-text text-transparent">{kpi}</span>
      </div>
      <div className="mt-0.5 text-[11px] uppercase tracking-wider text-white/60">{label}</div>
    </div>
  );
}
